import React from 'react';
import { useRouter } from 'next/router';

import { getPageBySlug, IPrismicPage } from 'providers/prismic/page';
import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { Layout } from 'containers/layout/Layout';
import { Slices } from 'containers/slices/Slices';
import { Error } from 'components/error/Error';
import { Header } from 'components/header/Header';
import { SplitPage } from 'components/split-page/SplitPage';
import { useUI } from 'utils/ui';
import { createSlug, findItem } from 'providers/prismic/utils/createSlug';
import { config } from 'utils/config';

type Props = {
  preview?: boolean;
  isTest: boolean;
  layout: IPrismicLayout;
  page: IPrismicPage;
  url: string | null;
};

export default function Page({ preview, isTest = false, layout, page, url }: Props) {
  const { isFallback, asPath } = useRouter();
  const ui = useUI();

  React.useEffect(() => {
    ui.toggleTest(isTest);
  }, [isTest]);

  if (isFallback) {
    return <Loading />;
  }

  if (!page) {
    return <Error title="Whoops!" statusCode={404} />;
  }

  if (page.type === 'split_page') {
    return (
      <Layout
        layout={layout}
        seo={{
          title: page.seo?.title || page.title,
          description: page.seo?.description,
          image: page.seo?.image,
          canonicalUrl: url,
        }}
        preview={preview}
        warning={page.options?.warning}
      >
        <SplitPage slices={page.slices} />
      </Layout>
    );
  }

  return (
    <Layout
      layout={layout}
      seo={{
        title: page.seo?.title || page.title,
        description: page.seo?.description,
        image: page.seo?.image,
        canonicalUrl: url,
      }}
      header={<Header showNavigation={layout.navigationMain.length > 0} />}
      hasFooter
      isPadded
      preview={preview}
      warning={page.options?.warning}
    >
      <Slices slices={page.slices} />
    </Layout>
  );
}

const Loading = () => <p>Loading...</p>;

export async function getStaticProps({ params, preview = false, previewData, locale }) {
  if (params.slug[0] === 'favicon.ico') {
    return {
      notFound: true,
    };
  }

  try {
    const lang = localeToLang(locale);

    /**
     * When the worker cycles through all the forms this is needed to disable reCAPTCHA
     */
    const isTest = previewData?.test || false;

    if (preview && previewData?.test) {
      preview = false;
      previewData = undefined;
    }

    const layout = await getLayout({ previewData, lang });
    const page = await getPageBySlug(params.slug[params.slug.length - 1], {
      previewData,
      lang,
      redirects: layout?.redirects,
    });

    const url = createSlug({
      includeSiteUrl: true,
      lang: lang,
      id: page?.id,
      uid: page?.uid,
      layouts: [layout],
    });

    /**
     * Checks if the route is nested and if the parent slug is correct
     */
    const redirect = layout.redirects?.find((x) => x.id === page?.id);
    const needsRedirect =
      redirect && params.slug.join('/') !== [redirect.parentUid, redirect.uid].join('/');

    if (redirect && needsRedirect) {
      return {
        redirect: {
          destination: redirect.to,
          permanent: true,
        },
      };
    }

    return {
      props: {
        isTest,
        preview,
        layout,
        page,
        url,
      },
      notFound: !page,
      revalidate: config.revalidate,
    };
  } catch (err) {
    console.log(err);
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
